import {
  DataMessageTypeEnum,
  type TerraUIMessage,
  type UserConfirmationMessage,
} from '@terra/shared-dto-chat-types';
import {
  getHITLTargetIds,
  getMetadataAsUserConfirmation,
  hasTextContent,
  isDataOrConfirmationPart,
  isHITLRequestConfirmation,
  parseBaseDataMessage,
} from './message-processing';

function backgroundSubagentHITLTargetIds(message: TerraUIMessage): string[] {
  return (message.parts ?? []).flatMap((part) => {
    if (!isDataOrConfirmationPart(part)) {
      return [];
    }

    const dataMessage = parseBaseDataMessage(part.data);

    return dataMessage &&
      isHITLRequestConfirmation(dataMessage) &&
      dataMessage.backgroundSubagent
      ? getHITLTargetIds({ dataMessage, messagePartId: part.id })
      : [];
  });
}

function answersOnlyBackgroundSubagentHITL({
  confirmation,
  earlierMessages,
}: {
  confirmation: UserConfirmationMessage;
  earlierMessages: TerraUIMessage[];
}): boolean {
  const subs = confirmation.subs ?? [];

  if (subs.length === 0) {
    return false;
  }

  const backgroundTargetIds = new Set(
    earlierMessages.flatMap(backgroundSubagentHITLTargetIds),
  );

  return subs.every((sub) => backgroundTargetIds.has(sub));
}

export function computeHasPendingHITL(messages: TerraUIMessage[]): boolean {
  let sawHITLConfirmation = false;
  let lastAssistantHasToolResult = false;

  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];

    if (msg.role === 'assistant' && !lastAssistantHasToolResult) {
      lastAssistantHasToolResult =
        msg.parts?.some((part) => {
          if (!isDataOrConfirmationPart(part) || !part.data) return false;

          return (
            part.data.type === DataMessageTypeEnum.ToolCallStart ||
            part.data.type === DataMessageTypeEnum.ToolCallComplete ||
            part.data.type === DataMessageTypeEnum.ToolCallUpdate ||
            part.data.type === DataMessageTypeEnum.ToolCallError
          );
        }) ?? false;
      if (lastAssistantHasToolResult) return false;
      if (msg.parts?.some(hasTextContent)) return false;
    }

    if (msg.role === 'user') {
      const confirmation = getMetadataAsUserConfirmation(msg.metadata);

      if (
        confirmation &&
        !answersOnlyBackgroundSubagentHITL({
          confirmation,
          earlierMessages: messages.slice(0, i),
        })
      ) {
        sawHITLConfirmation = true;
        break;
      }

      if (msg.parts?.some(hasTextContent)) {
        return false;
      }
    }
  }

  return sawHITLConfirmation && !lastAssistantHasToolResult;
}
